import {
    type AnyRole,
    Role,
    type Permission,
    isSuperUserRole,
    isRole,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import { createPermissionSet } from './permission-sets';
import type { AbilityChecker, AbilityContext, AbilityInspector } from './types';
import {
    createAbilityChecker,
    createAbilityInspector,
} from './definitions/evaluation';

export interface AuthContextOptions {
    roles?: Iterable<AnyRole> | null;
    extraPermissions?: Iterable<Permission> | null;
    userId?: string | null;
    teamId?: string | null;
    noImplicitRoles?: boolean;
    noPermissionFromRoles?: boolean;
}

function containsSuperUserRole(roles: Iterable<Role>) {
    for (const role of roles) {
        if (isSuperUserRole(role)) return true;
    }
    return false;
}

export class AuthContext implements AbilityContext {
    public readonly isSuperUser: boolean;

    public readonly userId: string | null;

    public readonly teamId: string | null;

    private readonly _roles: ReadonlySet<Role>;

    private readonly _permissions: ReadonlySet<Permission>;

    constructor(options: AuthContextOptions = {}) {
        this.userId = options.userId ?? null;
        this.teamId = options.teamId ?? null;

        const roles = new Set(options.roles) as Set<Role>;

        if (!options.noImplicitRoles) {
            if (this.userId) roles.add(Role.user);
            if (this.teamId) roles.add(Role.team);
        }

        this.isSuperUser = containsSuperUserRole(roles);

        this._roles = roles;
        this._permissions = options.noPermissionFromRoles
            ? new Set(options.extraPermissions)
            : createPermissionSet(roles, options.extraPermissions);
    }

    getPermissions(): ReadonlySet<Permission> {
        return this._permissions;
    }

    hasPermission(permission: Permission): boolean {
        if (this.isSuperUser) return true;
        return this._permissions.has(permission);
    }

    hasPermissions(...permissions: Permission[]): boolean {
        if (this.isSuperUser) return true;
        for (const permission of permissions) {
            if (!this._permissions.has(permission)) return false;
        }
        return true;
    }

    hasAnyPermission(...permissions: Permission[]): boolean {
        if (this.isSuperUser) return permissions.length > 0;
        for (const permission of permissions) {
            if (this._permissions.has(permission)) return true;
        }
        return false;
    }

    get hasTeam(): boolean {
        return this.teamId !== null;
    }

    get hasUser(): boolean {
        return this.userId !== null;
    }

    getRoles(): ReadonlySet<Role> {
        return this._roles;
    }

    hasRole(role: AnyRole): boolean {
        if (this.isSuperUser) return true;
        return this._roles.has(role as Role);
    }

    hasRoles(...roles: AnyRole[]): boolean {
        if (this.isSuperUser) return true;
        for (const role of roles) {
            if (!this._roles.has(role as Role)) return false;
        }
        return false;
    }

    hasAnyRole(...roles: AnyRole[]): boolean {
        if (this.isSuperUser) return roles.length > 0;
        for (const role of roles) {
            if (this._roles.has(role as Role)) return true;
        }
        return false;
    }

    has(...rolesPlusPermissions: (AnyRole | Permission)[]): boolean {
        if (this.isSuperUser) return true;
        for (const roleOrPermission of rolesPlusPermissions) {
            if (isRole(roleOrPermission)) {
                if (!this.hasRole(roleOrPermission)) return false;
            } else {
                if (!this.hasPermission(roleOrPermission as Permission))
                    return false;
            }
        }
        return true;
    }

    hasAny(...rolesPlusPermissions: (AnyRole | Permission)[]): boolean {
        if (this.isSuperUser) return rolesPlusPermissions.length > 0;
        for (const roleOrPermission of rolesPlusPermissions) {
            if (isRole(roleOrPermission)) {
                if (this.hasRole(roleOrPermission)) return true;
            } else {
                if (this.hasPermission(roleOrPermission as Permission))
                    return true;
            }
        }
        return false;
    }

    get inspect(): AbilityInspector {
        return createAbilityInspector(this);
    }

    get check(): AbilityChecker {
        return createAbilityChecker(this);
    }
}
